// @flow
import * as React from 'react';
import styled from 'styled-components';
import type { ThreeStepCtaBlockData } from 'api/cms';
import type { ImageData } from 'api/shared';
import { TrackedPrimaryButtonLink } from 'components/Button';
import { ConditionalLink } from 'components/Link';
import { FilestackImage } from 'components/Image';
import { H2, H3, Body, BodySmall } from 'components/Text';
import Box from 'components/Box';
import { breakpoints, colors, gutterWidth, transition } from '../../theme';

const Image = styled(({ large, ...props }) => (
  <FilestackImage quality={large ? undefined : 100} {...props} />
))`
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  &,
  * {
    height: 100%;
  }
  img {
    width: auto;
  }
  ${props =>
    props.large
      ? `
    border-radius: 10px;
    @supports (object-fit: cover) {
      position: relative;
      left: auto;
      top: 0;
      transform: none;
      &, * {
        width: 100%;
      }
      img {
        object-fit: cover;
        width: 100%;
      }
      svg {
        width: 40px;
      }
    }`
      : `
    @supports (object-fit: contain) {
      &, * {
        width: 100%;
      }
      img {
        object-fit: contain;
        width: 100%;
      }
      svg {
        width: 40px;
      }
    }
  `}
`;

const StepBox = styled(({ withLinks, url, large, ...props }) => {
  const onClick = () => {
    if (window && url) {
      window.location = url;
    }
  };

  return withLinks && !large ? (
    <Box
      onClick={url && onClick}
      padding={[24, null, 48]}
      width={1}
      height="100%"
      borderRadius={12}
      bg="white"
      boxShadow="0 0 20px rgba(0,0,0,0.1)"
      {...props}
    />
  ) : (
    <Box width={1} {...props} />
  );
})`
  ${props =>
    props.withLinks &&
    props.url &&
    !props.large &&
    `
      color: ${colors.greyishBrown};
      transition: all ${transition};
      cursor: pointer;
      a {
        color: inherit;
        text-decoration: none;
        transition: all ${transition};
        &:hover {
          text-decoration: none;
        }
      }
      &:hover {
        box-shadow: 0 0 6px rgba(0,0,0,0.2);
        a {
          color: ${colors.waterBlue};
        }
      }
    `}
`;

const CtaStep = styled(
  ({
    className,
    dataQaPrefix,
    headline,
    image,
    text,
    url,
    withImages,
    withLinks,
    large
  }: {
    className: string,
    dataQaPrefix: string,
    headline: string,
    image?: ImageData,
    text: string,
    url: string,
    withImages: boolean,
    withLinks: boolean,
    large?: boolean
  }) => (
    <Box
      maxWidth="400px"
      display="flex"
      alignItems="center"
      className={className}
      justifyContent="flex-start"
      flexDirection="column"
      flex="0 0 auto"
      width={[1, null, null, 1 / 3]}
      px={[0, null, null, gutterWidth / 2]}
      py={[gutterWidth / 2, null, null, 0]}
    >
      <StepBox url={url} withLinks={withLinks} large={large}>
        {(image || withImages) && (
          <Box
            position="relative"
            overflow="hidden"
            mb={24}
            width="100%"
            height={large ? 0 : 80}
            pb={large ? `${200 / 3}%` : 0}
          >
            {image && (
              <Box
                width="100%"
                height="100%"
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
                textAlign="center"
              >
                <ConditionalLink link={url} tabIndex="-1">
                  <Image
                    data-qa-id={`${dataQaPrefix}-image${large ? '-large' : ''}`}
                    alt={text}
                    data={image}
                    height={large ? 240 : 80}
                    width={352}
                    fit="max"
                    format={large ? 'jpg' : undefined}
                    large={large}
                  />
                </ConditionalLink>
              </Box>
            )}
          </Box>
        )}
        <Box width="100%" textAlign={large ? 'left' : 'center'}>
          <H3 mb="8px" styleAs={large ? 'h4' : 'h3'}>
            <ConditionalLink link={url}>{headline}</ConditionalLink>
          </H3>
          <BodySmall
            px={[0, null, large ? null : gutterWidth / 4]}
            mb={-16}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </Box>
      </StepBox>
    </Box>
  )
)`
  ${props =>
    !props.large &&
    !props.withLinks &&
    `
    border-bottom: solid 1px ${colors.silver};

    &:last-child {
      border-bottom: 0;
    }

    @media (min-width: ${breakpoints[2]}) {
      border-bottom: none;
      border-right: solid 1px ${colors.silver};

      &:last-child {
        border-right: 0;
      }
    }
  `}
`;

const STEPS = ['One', 'Two', 'Three'];

const ThreeStepCtaBlock = ({
  data,
  blockIndex,
  pageUpdated
}: {
  data: ThreeStepCtaBlockData,
  blockIndex: number,
  pageUpdated: string
}) => {
  const { mainHeadline, mainText, buttonUrl, buttonText, imageSize } = data;
  const withImages = STEPS.some(step => data[`step${step}Image`]);
  const withLinks = STEPS.some(step => data[`step${step}Url`]);
  return (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      flexDirection="column"
      justifyContent="center"
    >
      {(mainHeadline || mainText) && (
        <Box width="100%" textAlign="center" mb={24}>
          {mainHeadline && <H2 mt="0">{mainHeadline}</H2>}
          {mainText && <Body dangerouslySetInnerHTML={{ __html: mainText }} />}
        </Box>
      )}
      <Box
        display="flex"
        alignItems={['center', null, null, 'stretch']}
        flexDirection={['column', 'column', 'column', 'row']}
        justifyContent="center"
        mx={[0, null, null, -gutterWidth / 2]}
        width={[1, null, null, `calc(100% + ${gutterWidth}px)`]}
      >
        {STEPS.map(step => (
          <CtaStep
            dataQaPrefix={`step-${step.toLowerCase()}`}
            headline={data[`step${step}Headline`]}
            text={data[`step${step}Text`]}
            image={data[`step${step}Image`]}
            url={data[`step${step}Url`]}
            key={step}
            withImages={withImages}
            withLinks={withLinks}
            large={imageSize === 'LARGE'}
          />
        ))}
      </Box>
      {buttonText && buttonUrl && (
        <TrackedPrimaryButtonLink
          href={buttonUrl}
          mt={24}
          eventName="CMS Block"
          eventProperties={{
            block_position: blockIndex,
            block_type: 'Three Step Call to Action',
            cta_type: 'Button',
            page_updated: pageUpdated
          }}
        >
          {buttonText}
        </TrackedPrimaryButtonLink>
      )}
    </Box>
  );
};

export default ThreeStepCtaBlock;
